// Ortungsverfahren, en allemand. translationOf : page française équivalente.
import pressionImg from '../assets/controle_pression.webp';
import acoustiqueImg from '../assets/ecoute_acoustique.webp';
import fluoresceineImg from '../assets/fluoresceine_uv.webp';
import gazImg from '../assets/gaz_traceur_detecteur.webp';
import thermoImg from '../assets/thermographie_chauffage_sol.webp';
import humidimetreImg from '../assets/humidimetre_mur.webp';
import inspectionImg from '../assets/inspection_camera_egout.webp';

const PARENT = '/de/leckortung-verfahren';

const technique = (t) => ({ lang: 'de', parent: PARENT, eyebrow: 'Verfahren der Leckortung', ...t });

export const techniquesDe = [
  {
    path: '/de/leckortung-verfahren',
    lang: 'de',
    translationOf: '/techniques',
    parent: '/de/leckortung-luxemburg',
    nav: 'Unsere Verfahren',
    summary: 'Neun zerstörungsfreie Verfahren, je nach Leck ausgewählt und kombiniert.',
    title: 'Verfahren der zerstörungsfreien Leckortung | Expomis',
    description: 'Wärmebild, akustische Ortung, Tracergas, Fluoreszein, Rauchtest, Kanalkamera, Endoskopie, Druckprüfung, Feuchtemessung: welches Verfahren für welches Leck.',
    eyebrow: 'Verfahren der Leckortung',
    h1: 'Unsere Verfahren der Leckortung',
    intro: [
      'Es gibt kein Verfahren, das jedes Leck findet. Eine Wärmebildkamera sieht kein Kaltwasserleck, die akustische Ortung hört an einer Abwasserleitung nichts, und für Tracergas muss sich die Leitung absperren lassen. Expomis verfügt über neun zerstörungsfreie Verfahren und kombiniert sie, um die Ergebnisse abzusichern, bevor die Leckstelle markiert wird.',
    ],
    sections: [
      {
        h2: 'Welches Verfahren für welches Leck?',
        list: [
          'Bei jedem Einsatz: [Feuchtigkeitsmessung](/de/leckortung-verfahren/feuchtigkeitsmessung), um die Zone einzugrenzen, und [Druckprüfung](/de/leckortung-verfahren/druckpruefung), um das Leck zu bestätigen und den Kreislauf zu bestimmen.',
          'Warmwasser, Fußbodenheizung: [Wärmebildkamera](/de/leckortung-verfahren/waermebildkamera), danach [Tracergas](/de/leckortung-verfahren/tracergas), wenn der Kontrast nicht ausreicht.',
          'Kaltwasser unter Druck, erdverlegte Leitung: [akustische Ortung](/de/leckortung-verfahren/akustische-ortung) und Tracergas.',
          'Abwasser und Kanal: [Fluoreszein](/de/leckortung-verfahren/fluoreszein), [Rauchtest](/de/leckortung-verfahren/rauchtest), [Kanalkamera](/de/leckortung-verfahren/kanalkamera).',
          'Eindringendes Wasser: Feuchtigkeitsmessung, Fluoreszein, Wärmebildkamera.',
          'Verdeckte Bereiche (abgehängte Decke, Schacht, Vorsatzschale): [Endoskopie](/de/leckortung-verfahren/endoskopie).',
        ],
      },
    ],
    childrenTitle: 'Die neun Verfahren im Detail',
    children: [
      '/de/leckortung-verfahren/waermebildkamera',
      '/de/leckortung-verfahren/akustische-ortung',
      '/de/leckortung-verfahren/tracergas',
      '/de/leckortung-verfahren/fluoreszein',
      '/de/leckortung-verfahren/druckpruefung',
      '/de/leckortung-verfahren/feuchtigkeitsmessung',
      '/de/leckortung-verfahren/rauchtest',
      '/de/leckortung-verfahren/kanalkamera',
      '/de/leckortung-verfahren/endoskopie',
    ],
  },
  technique({
    path: '/de/leckortung-verfahren/waermebildkamera',
    translationOf: '/techniques/camera-thermique',
    nav: 'Wärmebildkamera',
    summary: 'Macht die Wärme eines Warmwasser- oder Fußbodenheizungslecks sichtbar, berührungslos.',
    serviceType: 'Leckortung mit Wärmebildkamera',
    title: 'Leckortung mit Wärmebildkamera (Thermografie) | Expomis',
    description: 'Wie die Wärmebildkamera ein Leck an Warmwasserleitung oder Fußbodenheizung ortet, wann sie funktioniert und wo ihre Grenzen liegen.',
    h1: 'Leckortung mit der Wärmebildkamera',
    image: thermoImg,
    imageAlt: 'Wärmebild mit den Rohren einer Fußbodenheizung und dem warmen Fleck eines Lecks',
    intro: [
      'Die Infrarot-Thermografie misst berührungslos die Temperatur von Oberflächen und macht daraus ein Bild. Tritt warmes Wasser aus einer Leitung oder einer Fußbodenheizung aus, erwärmt es den Estrich oder die Wand rund um die Leckstelle: Die Wärmebildkamera macht diesen Fleck sichtbar, ohne etwas anzufassen.',
    ],
    sections: [
      {
        h2: 'So funktioniert es',
        body: [
          'Die Kamera sieht nicht durch Wände: Sie liest die Temperatur der Oberfläche, und was dahinter geschieht, ergibt sich aus den Temperaturunterschieden. Bei einem Warmwasser- oder Heizungsleck wird der Kreislauf aufgeheizt und die Ausbreitung der Wärme beobachtet: Zuerst erscheint der Verlauf der Rohre, dann die Zone, in der sich das Wasser verteilt.',
          'An einer nassen Wand ist es umgekehrt: Die Verdunstung kühlt die Oberfläche, und die feuchte Zone erscheint kälter als der Rest.',
        ],
      },
      {
        h2: 'Wann sie zum Einsatz kommt',
        list: [
          '[Fußbodenheizung](/de/leckortung-heizung) und Heizungsleitungen;',
          'Warmwasserleitungen unter Putz;',
          'Ausdehnung einer feuchten Zone nach [eindringendem Wasser](/de/ortung-wassereintritt) oder einem Wasserschaden.',
        ],
      },
      {
        h2: 'Grenzen',
        body: [
          'Es braucht einen Temperaturunterschied. Ein Kaltwasserleck in einer Wand mit Raumtemperatur ist schwer zu erkennen. Ein dicker Belag oder ein Parkett auf Lagerhölzern schwächt das Signal ab, und glänzende Oberflächen spiegeln die Strahlung. In diesen Fällen greifen wir auf [Tracergas](/de/leckortung-verfahren/tracergas) oder die [akustische Ortung](/de/leckortung-verfahren/akustische-ortung) zurück.',
        ],
      },
    ],
    faq: [
      {
        q: 'Sieht die Wärmebildkamera durch Wände?',
        a: 'Nein. Sie misst die Temperatur der Oberfläche. Erst das austretende warme Wasser verändert diese Temperatur und verrät so die Lage des Lecks.',
      },
    ],
    related: ['/de/leckortung-heizung', '/de/leckortung-verfahren/tracergas', '/de/leckortung-verfahren/feuchtigkeitsmessung'],
  }),
  technique({
    path: '/de/leckortung-verfahren/akustische-ortung',
    translationOf: '/techniques/detection-acoustique',
    nav: 'Akustische Ortung',
    summary: 'Elektroakustik und Korrelation: das Geräusch des austretenden Wassers unter Druck.',
    serviceType: 'Akustische Leckortung',
    title: 'Akustische Leckortung | Expomis',
    description: 'Elektroakustische Ortung und Korrelation: wie ein Leck an einer Druckleitung unter Putz oder im Erdreich geortet wird, und die Grenzen des Verfahrens.',
    h1: 'Akustische Leckortung',
    image: acoustiqueImg,
    imageAlt: 'Illustration: elektroakustische Ortung an einer erdverlegten Leitung mit Bodenmikrofon',
    intro: [
      'Eine undichte Druckleitung macht Geräusche: Das Wasser, das durch einen Riss austritt, zischt oder rauscht, und das Rohr und der Boden übertragen dieses Geräusch. Die elektroakustische Ortung verstärkt es, um die Stelle zu finden, an der es am lautesten ist, also direkt über dem Leck.',
    ],
    sections: [
      {
        h2: 'So funktioniert es',
        body: [
          'Ein sehr empfindliches Bodenmikrofon wird Punkt für Punkt entlang des Leitungsverlaufs aufgesetzt. Das Gerät verstärkt und filtert das Signal, um das Leckgeräusch von Störgeräuschen zu trennen. Je näher man dem Leck kommt, desto lauter wird es.',
          'Bei längeren Leitungen setzt die Korrelation zwei Sensoren beiderseits der verdächtigen Zone. Aus dem Zeitunterschied, mit dem das Geräusch bei jedem Sensor ankommt, lässt sich die Lage des Lecks berechnen.',
        ],
      },
      {
        h2: 'Wann sie zum Einsatz kommt',
        list: [
          'Trinkwasserleitungen unter Druck, auch Kaltwasser;',
          '[erdverlegte Leitungen](/de/leckortung-wasserleitung) unter Garten oder Einfahrt;',
          'Leitungen unter der Bodenplatte.',
        ],
      },
      {
        h2: 'Grenzen',
        body: [
          'Das Verfahren braucht Druck: An [Abwasserleitungen](/de/leckortung-abwasser) funktioniert es nicht. Sehr kleine Lecks sind leise, und Umgebungslärm (Verkehr, Pumpen, laufende Geräte) stört die Messung. Weiche Böden und lockere Erde dämpfen den Schall. In diesen Fällen übernimmt das [Tracergas](/de/leckortung-verfahren/tracergas).',
        ],
      },
    ],
    related: ['/de/leckortung-wasserleitung', '/de/leckortung-verfahren/tracergas', '/de/hoher-wasserverbrauch'],
  }),
  technique({
    path: '/de/leckortung-verfahren/tracergas',
    translationOf: '/techniques/gaz-traceur',
    nav: 'Tracergas',
    summary: 'Stickstoff-Wasserstoff-Gemisch, das genau über den kleinsten Lecks an die Oberfläche steigt.',
    serviceType: 'Leckortung mit Tracergas',
    title: 'Leckortung mit Tracergas (Stickstoff-Wasserstoff) | Expomis',
    description: 'Tracergas aus Stickstoff und Wasserstoff ortet kleine Lecks unter dem Estrich oder im Erdreich. Funktionsweise, Sicherheit, Einsatzfälle und Grenzen.',
    h1: 'Leckortung mit Tracergas',
    image: gazImg,
    imageAlt: 'Wasserstoffdetektor und Bodensonde auf Fliesen bei der Leckortung mit Tracergas',
    intro: [
      'Tracergas ist ein Gemisch aus Stickstoff und Wasserstoff, in diesem Verhältnis ungiftig und nicht brennbar. In eine Leitung eingebracht, entweicht es durch das Leck, dringt durch Estrich, Fliesen oder Erdreich, und ein sehr empfindlicher Detektor erfasst es an der Oberfläche, genau über der Leckstelle.',
    ],
    sections: [
      {
        h2: 'So funktioniert es',
        body: [
          'Das Wasser wird abgestellt und der betroffene Abschnitt entleert, dann wird das Gas unter Druck eingebracht. Wasserstoff ist das kleinste und leichteste Molekül: Er durchdringt die Baustoffe und steigt zur Oberfläche. Anschließend wird der Leitungsverlauf mit einer Sonde abgesucht; die höchste Konzentration zeigt das Leck an.',
        ],
      },
      {
        h2: 'Wann es zum Einsatz kommt',
        list: [
          'kleine, leise Lecks, die die akustische Ortung nicht erfasst;',
          '[Leitungen unter der Bodenplatte oder im Erdreich](/de/leckortung-wasserleitung);',
          '[Fußbodenheizung](/de/leckortung-heizung), wenn die Wärmebildkamera keinen ausreichenden Kontrast liefert.',
        ],
      },
      {
        h2: 'Grenzen',
        body: [
          'Der geprüfte Abschnitt muss sich absperren und entleeren lassen. Manche sehr dichte Beläge bremsen den Aufstieg des Gases, und Luftzug verteilt es. Für Abwasserleitungen, die ihrer Natur nach nicht gasdicht sind, eignet sich das Verfahren nicht: Dort setzen wir den [Rauchtest](/de/leckortung-verfahren/rauchtest) ein.',
        ],
      },
    ],
    faq: [
      {
        q: 'Ist Tracergas gefährlich?',
        a: 'Nein. Das verwendete Gemisch enthält 95 % Stickstoff und 5 % Wasserstoff: Es ist nicht brennbar, ungiftig, geruchlos und verflüchtigt sich in der Luft.',
      },
    ],
    related: ['/de/leckortung-wasserleitung', '/de/leckortung-verfahren/akustische-ortung', '/de/leckortung-verfahren/druckpruefung'],
  }),
  technique({
    path: '/de/leckortung-verfahren/fluoreszein',
    translationOf: '/techniques/fluoresceine',
    nav: 'Fluoreszein',
    summary: 'Fluoreszierender Farbstoff, der zeigt, woher das Wasser kommt: Abfluss, Dusche, Terrasse.',
    serviceType: 'Leckortung mit Fluoreszein',
    title: 'Leckortung mit Fluoreszein | Expomis',
    description: 'Der Fluoreszein-Test bestimmt die Ursache eines Lecks an Abwasser, Dusche oder Terrasse. Prinzip, Einsatzfälle und Grenzen.',
    h1: 'Leckortung mit Fluoreszein',
    image: fluoresceineImg,
    imageAlt: 'Fluoreszein unter UV-Licht: gefärbtes Wasser tritt an einem Siphonanschluss aus und läuft über die Fliesen',
    intro: [
      'Fluoreszein ist ein ungiftiger, grün fluoreszierender Farbstoff, der schon in sehr geringer Konzentration sichtbar ist, vor allem unter UV-Licht. Man färbt das Wasser eines Geräts oder einer bestimmten Zone und beobachtet, wo es wieder auftaucht: Erscheint das gefärbte Wasser im Feuchtigkeitsfleck, ist die Ursache des Lecks gefunden.',
    ],
    sections: [
      {
        h2: 'So funktioniert es',
        body: [
          'Die Prüfung erfolgt Quelle für Quelle: Duschablauf, Waschbecken, WC, Terrassenablauf, Abdichtungsanschluss. Das gefärbte Wasser läuft immer nur an einer Stelle, und die geschädigte Zone wird beobachtet. Sobald die Farbe erscheint, ist die Quelle bestimmt.',
        ],
      },
      {
        h2: 'Wann es zum Einsatz kommt',
        list: [
          '[Abwasserleitungen](/de/leckortung-abwasser): welches Gerät, welcher Anschluss betroffen ist;',
          '[Duschen und Badewannen](/de/leckortung-dusche): Ablauf oder Abdichtung;',
          '[eindringendes Wasser](/de/ortung-wassereintritt) über Terrasse, Balkon oder Flachdach, durch gezielte Bewässerung;',
          'Kontrolle eines Anschlusses: Ist ein Gerät wirklich an den Kanal angeschlossen?',
        ],
      },
      {
        h2: 'Grenzen',
        body: [
          'Fluoreszein bestimmt die Quelle, nicht die genaue Leckstelle entlang des Leitungswegs. Außerdem muss das Wasser sichtbar wieder austreten: Versickert es im Boden, sieht man nichts, und wir gehen zur [Kanalkamera](/de/leckortung-verfahren/kanalkamera) oder zum [Rauchtest](/de/leckortung-verfahren/rauchtest) über.',
        ],
      },
    ],
    faq: [
      {
        q: 'Ist Fluoreszein schädlich?',
        a: 'Nein. Es wird in sehr geringer Konzentration eingesetzt und dient auch zur Markierung von Grundwasser, gerade weil es in diesen Mengen für die Umwelt unbedenklich ist.',
      },
    ],
    related: ['/de/leckortung-dusche', '/de/leckortung-abwasser', '/de/ortung-wassereintritt'],
  }),
  technique({
    path: '/de/leckortung-verfahren/druckpruefung',
    translationOf: '/techniques/mise-en-pression',
    nav: 'Druckprüfung',
    summary: 'Bestätigt das Leck und bestimmt den betroffenen Kreislauf, vor jeder Ortung.',
    serviceType: 'Druckprüfung von Leitungen',
    title: 'Druckprüfung: ein Leck bestätigen und eingrenzen | Expomis',
    description: 'Die Druckprüfung bestätigt ein Leck und bestimmt den betroffenen Kreislauf: Kaltwasser, Warmwasser, Heizung, Fußbodenheizung. Prinzip und Grenzen.',
    h1: 'Druckprüfung: ein Leck bestätigen und eingrenzen',
    image: pressionImg,
    imageAlt: 'Illustration: Druckprüfung einer Leitung mit dem Manometer',
    intro: [
      'Die Druckprüfung ist oft der erste Schritt einer Leckortung. Ein Kreislauf wird abgesperrt, unter Druck gesetzt und am Manometer beobachtet: Fällt der Druck, ist der Kreislauf undicht. Das ist einfach, aber unverzichtbar: Es bestätigt, dass tatsächlich ein Leck vorliegt, und zeigt, in welchem Kreislauf es zu suchen ist.',
    ],
    sections: [
      {
        h2: 'So funktioniert es',
        body: [
          'Durch Schließen der Ventile wird jeder Kreislauf einzeln abgesperrt: Kaltwasser, Warmwasser, Zirkulationsleitung, Heizung und jeder Heizkreis einer Fußbodenheizung am Verteiler. Jeder wird auf einen Prüfdruck gebracht und über eine bestimmte Zeit beobachtet. Der Kreislauf, dessen Druck fällt, ist der undichte.',
        ],
      },
      {
        h2: 'Wann sie zum Einsatz kommt',
        list: [
          '[erhöhter Wasserverbrauch](/de/hoher-wasserverbrauch) ohne sichtbare Spuren;',
          '[Heizung, die Druck verliert](/de/leckortung-heizung);',
          'vor einer Ortung mit Wärmebildkamera oder Tracergas, um den richtigen Kreislauf anzugehen;',
          'nach der Reparatur, um zu prüfen, ob der Kreislauf wieder dicht ist.',
        ],
      },
      {
        h2: 'Grenzen',
        body: [
          'Die Druckprüfung zeigt, ob ein Kreislauf undicht ist, nicht wo genau. Für Abwasserleitungen, die nicht unter Druck stehen, eignet sie sich nicht. Ein Ventil, das nicht mehr richtig schließt, kann das Ergebnis verfälschen: Das berücksichtigen wir bei der Auswertung.',
        ],
      },
    ],
    related: ['/de/leckortung-verfahren/tracergas', '/de/leckortung-verfahren/akustische-ortung', '/de/hoher-wasserverbrauch'],
  }),
  technique({
    path: '/de/leckortung-verfahren/feuchtigkeitsmessung',
    translationOf: '/techniques/humidimetre',
    nav: 'Feuchtigkeitsmessung',
    summary: 'Kartierung der feuchten Zone: ihre Ausdehnung und die Seite, von der das Wasser kommt.',
    serviceType: 'Feuchtigkeitsmessung an Baustoffen',
    title: 'Feuchtigkeitsmessung: eine feuchte Zone kartieren | Expomis',
    description: 'Feuchtigkeitsmessung: eine feuchte Zone eingrenzen, Leck, eindringendes Wasser und aufsteigende Feuchtigkeit unterscheiden, die Trocknung verfolgen.',
    h1: 'Feuchtigkeitsmessung: eine feuchte Zone kartieren',
    image: humidimetreImg,
    imageAlt: 'Feuchtigkeitsmessgerät an einer Wand mit Feuchtigkeitsfleck und abblätternder Farbe',
    intro: [
      'Das Feuchtigkeitsmessgerät misst den Feuchtegehalt eines Baustoffs: Putz, Beton, Estrich, Holz, Mauerwerk. Mit Messungen an vielen Punkten entsteht eine Karte der feuchten Zone: ihre Ausdehnung und vor allem ihr Verlauf, der zeigt, von welcher Seite das Wasser kommt.',
    ],
    sections: [
      {
        h2: 'So funktioniert es',
        body: [
          'Je nach Baustoff wird zerstörungsfrei an der Oberfläche gemessen oder, bei Holz und für Messungen in der Tiefe, mit Messspitzen. Die Werte werden in einem regelmäßigen Raster aufgenommen. Die höchsten Werte liegen am nächsten an der Wasserquelle.',
        ],
      },
      {
        h2: 'Wann sie zum Einsatz kommt',
        list: [
          'zu Beginn jedes Einsatzes, um die betroffene Zone einzugrenzen;',
          'um ein Leck von [eindringendem Wasser](/de/ortung-wassereintritt), Kondenswasser oder aufsteigender Feuchtigkeit zu unterscheiden;',
          'um die Schäden in einer [Wasserschadenakte](/de/wasserschaden) zu dokumentieren;',
          'um nach der Reparatur die Trocknung zu verfolgen, bevor die Oberflächen erneuert werden.',
        ],
      },
      {
        h2: 'Grenzen',
        body: [
          'Das Messgerät zeigt, wo sich das Wasser befindet, aber nicht sicher, woher es kommt: Es lenkt die Suche, die andere Verfahren bestätigen. Die Werte hängen vom Baustoff ab; man vergleicht sie untereinander auf demselben Untergrund statt als absolute Werte.',
        ],
      },
    ],
    related: ['/de/ortung-wassereintritt', '/de/wasserschaden', '/de/leckortung-verfahren/druckpruefung'],
  }),
  technique({
    path: '/de/leckortung-verfahren/rauchtest',
    translationOf: '/techniques/test-fumigene',
    nav: 'Rauchtest',
    summary: 'Ungiftiger Rauch, der an den Schwachstellen einer Abwasserleitung austritt und Gerüche aufklärt.',
    serviceType: 'Rauchtest an Abwasserleitungen',
    title: 'Rauchtest: Lecks am Abwasser und Kanalgeruch | Expomis',
    description: 'Der Rauchtest zeigt undichte Verbindungen, Risse und Mängel einer Abwasser- oder Kanalleitung sowie die Ursache von Gerüchen. Prinzip und Grenzen.',
    h1: 'Rauchtest: Mängel an Abwasserleitungen finden',
    intro: [
      'Beim Rauchtest wird ungiftiger Rauch in eine Abwasser- oder Kanalleitung geleitet. Der Rauch folgt dem Leitungsnetz und tritt an allen Schwachstellen aus: schlecht gesteckte Verbindung, gerissenes Rohr, fehlender Siphon, undichter Anschluss. Wo der Rauch erscheint, entweicht Wasser oder dringt Geruch ein.',
    ],
    sections: [
      {
        h2: 'So funktioniert es',
        body: [
          'Ein Generator leitet den Rauch mit leichtem Überdruck über einen Schacht oder eine Revisionsöffnung in das Netz. Dann wird das Gebäude abgegangen: Räume, Schächte, Keller, Fuß der Fallrohre, Außenbereiche. Jeder Rauchaustritt zeigt eine Undichtigkeit. Auch ein ausgetrockneter oder fehlender Siphon lässt Rauch durch, was oft Kanalgeruch ohne Wasserleck erklärt.',
        ],
      },
      {
        h2: 'Wann er zum Einsatz kommt',
        list: [
          'Kanalgeruch in einer Wohnung oder im Keller;',
          '[Lecks an Abwasserleitungen](/de/leckortung-abwasser) und Fallrohren;',
          'Kontrolle der Anschlüsse und der Entlüftung des Netzes.',
        ],
      },
      {
        h2: 'Grenzen',
        body: [
          'Das Netz muss über einen Schacht, einen Deckel oder eine Revisionsöffnung zugänglich sein. Tritt der Rauch in einem nicht einsehbaren Bereich aus, etwa unter einer Bodenplatte, ergänzen wir mit der [Kanalkamera](/de/leckortung-verfahren/kanalkamera).',
        ],
      },
    ],
    faq: [
      {
        q: 'Ist der Rauch gefährlich?',
        a: 'Nein. Er ist ungiftig und verflüchtigt sich schnell; nach dem Test genügt es zu lüften.',
      },
    ],
    related: ['/de/leckortung-abwasser', '/de/leckortung-verfahren/kanalkamera', '/de/leckortung-verfahren/fluoreszein'],
  }),
  technique({
    path: '/de/leckortung-verfahren/kanalkamera',
    translationOf: '/techniques/camera-canalisation',
    nav: 'Kanalkamera',
    summary: 'Kamera im Abwasserrohr: Risse, verschobene Muffen, Wurzeln, Gegengefälle.',
    serviceType: 'Kamerainspektion von Leitungen',
    title: 'Kamerainspektion von Abwasserleitungen und Kanal | Expomis',
    description: 'Die Kamerainspektion filmt das Innere von Abwasserleitungen und Kanälen: Risse, verschobene Muffen, Wurzeln, Verformungen, Gegengefälle. Prinzip und Grenzen.',
    h1: 'Kamerainspektion von Abwasserleitungen und Kanal',
    image: inspectionImg,
    imageAlt: 'Bildschirm einer Kanalkamera mit einem Riss im Rohr, Kabel in einen Schacht hinabgelassen',
    intro: [
      'Eine wasserdichte Kamera wird am Ende eines Schubkabels in das Abwassernetz geschoben und filmt das Innere der Leitungen. Man sieht direkt den Zustand des Rohrs: Risse, verschobene Muffen, Wurzeln, Verformungen, Ablagerungen, Gegengefälle. Die abgerollte Kabellänge zeigt, wie weit der Schaden vom Zugangspunkt entfernt ist.',
    ],
    sections: [
      {
        h2: 'So funktioniert es',
        body: [
          'Die Kamera wird über einen Schacht, einen Deckel oder ein Reinigungsstück eingeführt und schrittweise in die Leitung geschoben. Das Bild erscheint live auf einem Bildschirm, und der Kabelzähler zeigt, in welcher Entfernung vom Einstiegspunkt jeder Schaden liegt. So weiß man, welcher Abschnitt betroffen ist und wo geöffnet werden muss, statt die ganze Leitung freizulegen.',
          'Anders als Fluoreszein oder Rauch, die zeigen, dass ein Mangel besteht, zeigt die Kamera seine Art: Riss, verschobene Muffe oder Wurzel verlangen nicht dieselbe Reparatur.',
        ],
      },
      {
        h2: 'Wann sie zum Einsatz kommt',
        list: [
          '[erdverlegte oder unter der Bodenplatte liegende Abwasserleitungen](/de/leckortung-abwasser);',
          'Anschluss des Hauses an den Kanal;',
          'nach einem Rauch- oder Fluoreszein-Test, um den Mangel zu sehen und über die Reparatur zu entscheiden;',
          'langsamer Abfluss oder wiederholter Rückstau.',
        ],
      },
      {
        h2: 'Grenzen',
        body: [
          'Es braucht einen Zugang: Schacht, Deckel, Reinigungsstück. Kleine Durchmesser und enge Bögen begrenzen das Vorankommen, und eine verstopfte Leitung muss zuerst gereinigt werden.',
        ],
      },
    ],
    related: ['/de/leckortung-abwasser', '/de/leckortung-verfahren/rauchtest', '/de/leckortung-verfahren/endoskopie'],
  }),
  technique({
    path: '/de/leckortung-verfahren/endoskopie',
    translationOf: '/techniques/endoscopie',
    nav: 'Endoskopie',
    summary: 'In eine abgehängte Decke, einen Schacht oder eine Vorsatzschale sehen, ohne abzubauen.',
    serviceType: 'Endoskopie',
    title: 'Endoskopie: unzugängliche Bereiche prüfen | Expomis',
    description: 'Mit dem Endoskop in eine abgehängte Decke, einen Installationsschacht oder unter eine Badewanne sehen, ohne abzubauen. Einsatz und Grenzen bei der Leckortung.',
    h1: 'Endoskopie: unzugängliche Bereiche prüfen',
    intro: [
      'Ein Endoskop ist eine kleine Kamera am Ende einer flexiblen Sonde. Sie wird durch eine vorhandene Öffnung (Revisionsklappe, Lüftungsauslass, Fuge, Befestigungsloch) oder durch eine kleine Bohrung eingeführt, um in eine abgehängte Decke, einen Installationsschacht, eine Vorsatzschale oder unter eine Badewanne zu sehen, ohne abzubauen.',
    ],
    sections: [
      {
        h2: 'So funktioniert es',
        body: [
          'Die Sonde wird in den Hohlraum geführt, während das Bild auf einem Bildschirm erscheint. So lässt sich eine Leitung verfolgen, ein tropfender Anschluss, Laufspuren oder ein nasser Baustoff erkennen und für den Bericht fotografieren. Ist eine kleine Bohrung nötig, lässt sie sich leicht wieder verschließen, kein Vergleich mit dem Abbau einer Verkleidung oder einer Decke.',
        ],
      },
      {
        h2: 'Wann sie zum Einsatz kommt',
        list: [
          'einen verdeckten Anschluss hinter einer Badewannenverkleidung oder einem WC-Vorwandelement prüfen;',
          'einen Installationsschacht in einem Mehrfamilienhaus inspizieren;',
          'den Weg des Wassers in einer abgehängten Decke oder einem Hohlraum verfolgen;',
          'ein mit einem anderen Verfahren geortetes Leck optisch bestätigen.',
        ],
      },
      {
        h2: 'Grenzen',
        body: [
          'Das Endoskop sieht nur, was in einem Hohlraum liegt: Für eine Leitung in einem vollen Estrich ist es nutzlos. Seine Reichweite beträgt wenige Meter ab dem Einstiegspunkt.',
        ],
      },
    ],
    related: ['/de/ortung-wassereintritt', '/de/leckortung-dusche', '/de/leckortung-verfahren/kanalkamera'],
  }),
];
